import { t } from '@lingui/core/macro';
import { linkOptions, useCanOpen } from '@luminovo/design-system';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import type { JSX } from 'react';

import { SettingsSidebar, useSettingsEntry } from '@/app/navigation/SettingsSidebar';
import { SecondSidebar } from '@/app/NewNavigation/NewNavigationShell';
import { SettingsTrail } from '@/areas/settings/Settings/components/SettingsPage';
import type { PageStaticData } from '@/shared/routing';
import { SidebarRouteLayout } from '@/shared/ui/PageLayoutCollapsibleSidebar';

// The pages beside the settings sidebar, which stays mounted across them.
// Pathless, as today's layout is: account security sits at `/settings/user`, not below `/organization`.
export const Route = createFileRoute('/_authenticated/next/settings/_menu')({
    // The access every page shares; each page declares its own as well.
    staticData: { access: { permissions: ['view:organization_settings'] }, chrome: 'none' } satisfies PageStaticData,
    component: RouteComponent,
});

/** The settings sidebar beside the app's, as a second gray one, and the way to the page in each page's header. */
function RouteComponent(): JSX.Element {
    const canOpen = useCanOpen();
    const entry = useSettingsEntry();
    // A group links to the first of its pages the user may open, as its rail entry does.
    const trail =
        entry === undefined
            ? []
            : [
                  { label: t`Settings`, link: linkOptions({ to: '/settings/organization' }) },
                  {
                      label: entry.group.label,
                      link: entry.group.pages.find(({ link }) => canOpen(link))?.link ?? entry.page.link,
                  },
                  // The page a page below another one belongs to, such as PCB for a panel preference.
                  ...(entry.isPage ? [] : [entry.page]),
              ];
    return (
        <SettingsTrail.Provider value={trail}>
            <SecondSidebar>
                <nav aria-label={t`Settings`} style={{ display: 'flex' }}>
                    <SettingsSidebar />
                </nav>
            </SecondSidebar>
            {/* The pages' row and scroll area in the panel, as beside today's sidebar. */}
            <SidebarRouteLayout sidebar={null}>
                <Outlet />
            </SidebarRouteLayout>
        </SettingsTrail.Provider>
    );
}
